import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import API_ENDPOINTS from '@/apis/api-endpoints';

import type {
  BoardContentDetail,
  BoardContentUpdateType,
} from '@/types/boardContent';

import {
  createNonUserBoardContent,
  getBoardContentsByBdId,
  getBoardContentsDetailByBcidx,
} from './boardContents';
import type {
  BoardContentsDetailQueryType,
  BoardContentsInfinityQueryType,
  BoardContentsQueryType,
  BoardContentsType,
  MultipleBoardContentsSearchQueriesType,
  SingleBoardContentsSearchQueryType,
} from './types';

/**
 * 게시글 리스트 쿼리
 */
export const useBoardContentsByBdIdQuery = ({
  params,
  useQueryOptions,
}: BoardContentsQueryType) => {
  return useQuery<BoardContentsType, AxiosError>({
    queryKey: [API_ENDPOINTS.BOARD_CONTENTS, params],
    queryFn: () => getBoardContentsByBdId(params || {}),
    ...useQueryOptions,
  });
};

/**
 * 게시글 리스트 무한 스크롤 쿼리
 */
export const useInfiniteBoardContentsByBdIdQuery = ({
  params,
  initialData,
}: BoardContentsInfinityQueryType) => {
  return useInfiniteQuery<BoardContentsType, AxiosError>({
    queryKey: [API_ENDPOINTS.BOARD_CONTENTS, params],
    queryFn: ({ pageParam = 1 }) =>
      getBoardContentsByBdId({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage.next || undefined;
    },
    keepPreviousData: true,
    staleTime: 0,
    initialData,
  });
};

/**
 * 게시글 리스트 갱신
 */
export const useInvalidateBoardContentsList = () => {
  const queryClient = useQueryClient();
  const invalid = () => {
    queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.BOARD_CONTENTS],
    });
  };
  return invalid;
};

/**
 * 게시글 상세 쿼리
 */
export const useBoardContentsDetailByBcidxQuery = ({
  params,
  useQueryOptions,
}: BoardContentsDetailQueryType) => {
  return useQuery<BoardContentDetail, AxiosError>({
    queryKey: [API_ENDPOINTS.BOARD_CONTENTS, params],
    queryFn: () => getBoardContentsDetailByBcidx(params || {}),
    ...useQueryOptions,
  });
};

/**
 * 게시글 상세 API 갱신
 */
export const useInvalidateBoardContentsDetail = () => {
  const queryClient = useQueryClient();
  const invalid = (bcIdx: string) => {
    queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.BOARD_CONTENTS, { bc_idx: bcIdx }],
    });
  };
  return invalid;
};

/**
 * POST
 * /board-contents
 * 게시글 생성 API 뮤테이션
 */
export const useCreateBoardContentsMutation = () => {
  return useMutation({
    mutationFn: (body: BoardContentUpdateType) =>
      createNonUserBoardContent(body),
  });
};

/**
 * 다중 게시판 검색 결과 useQueries
 */
export const useSearchBoardContentsByBdidQueries = ({
  params,
  useQueryOptions,
}: MultipleBoardContentsSearchQueriesType) => {
  return useQueries({
    queries: params.bd_ids.map((bd_id) => ({
      queryKey: [
        API_ENDPOINTS.BOARD_CONTENTS,
        { bd_id: bd_id, search: params.search, page: params.page },
      ],
      queryFn: () => getBoardContentsByBdId({ ...params, bd_id: bd_id }),
      ...useQueryOptions,
    })),
  });
};

/**
 * 단일 게시판 검색 결과 useQuery
 */
export const useSearchBoardContentsByBdidxQuery = ({
  params,
  useQueryOptions,
}: SingleBoardContentsSearchQueryType) => {
  return useQuery({
    queryKey: [
      API_ENDPOINTS.BOARD_CONTENTS,
      { bd_id: params.bd_id, search: params.search, page: params.page },
    ],
    queryFn: () => getBoardContentsByBdId({ ...params }),
    ...useQueryOptions,
  });
};
